import { Formik, Form, FormikHelpers } from "formik";
import { useState } from "react";
import Modal from "../Modal";
import Toast from "../../Toast/Toast";
import DynamicField from "../../DynamicForm/DynamicField";
import { useLanguage } from "../../../context/LanguageContext";
import { AuthService } from "../../../service/auth.service";
import styles from "./RegisterModal.module.css";
import { getRegisterSchema, RegisterFormValues } from "../../../utils/validationSchema";

type AuthMode = "login" | "register" | "forgot";

interface ToastState {
  message: string;
  type: "success" | "error" | "info";
}

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchMode: (mode: AuthMode) => void;
  registrationEnabled?: boolean;
  tgBot?: string;
}

export default function RegisterModal({
  isOpen,
  onClose,
  onSwitchMode,
  tgBot = '',
  registrationEnabled = true,
}: RegisterModalProps) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [registeredLogin, setRegisteredLogin] = useState("");
  const { language, t } = useLanguage();

  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "success"
  ) => {
    setToast({ message, type });
  };

  const getErrorMessage = (error: any): string => {
    const errorKey = error.response?.data?.error;

    if (errorKey) {
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }
    }

    const apiMessage = error.response?.data?.message;
    if (apiMessage) {
      const translated = t(apiMessage);
      if (translated !== apiMessage) {
        return translated;
      }
    }

    const errorMessage = error.message;
    if (errorMessage) {
      const translated = t(errorMessage);
      if (translated !== errorMessage) {
        return translated;
      }
    }

    return t("registrationFailed");
  };

  const handleClose = () => {
    setShowSuccessMessage(false);
    setRegisteredLogin("");
    onClose();
  };

  if (!registrationEnabled) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t("registrationDisabled")}
        closeOnOverlayClick={false}
      >
        <div className={styles.disabledMessage}>
          <div className={styles.disabledIcon}>
            <i className="fa fa-lock"></i>
          </div>
          <p className={styles.disabledText}>
            {t("registrationCurrentlyDisabled")}
          </p>
          <button
            className={styles.submitButton}
            onClick={() => onSwitchMode("login")}
          >
            {t("backToLogin")}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={showSuccessMessage ? t("registrationComplete") : t("registerTitle")}
        closeOnOverlayClick={false}
      >
        {showSuccessMessage ? (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>
              <i className="fa fa-check-circle"></i>
            </div>
            <h3>{t("registrationSuccess")}</h3>
            <div className={styles.infoBox}>
              <p>
                <strong>{t("yourLogin")}:</strong> {registeredLogin}
              </p>
              <p className={styles.warningText}>
                <i className="fa fa-exclamation-triangle"></i> {t("accountAwaitingActivation")}
              </p>
              <p className={styles.helpText}>
                {t("activationInstructions")}
              </p>
            </div>
            <button
              className={styles.submitButton}
              onClick={() => {
                setShowSuccessMessage(false);
                setRegisteredLogin("");
                onSwitchMode("login");
              }}
            >
              {t("understood")}
            </button>
          </div>
        ) : (
          <Formik
            initialValues={{
              nickname: "",
              email: "",
              telegramId: "",
              password: "",
              confirmPassword: "",
            }}
            validationSchema={getRegisterSchema(t)}
            onSubmit={async (
              values: RegisterFormValues,
              { setSubmitting, resetForm }: FormikHelpers<RegisterFormValues>
            ) => {
              try {
                const login = values.nickname.toLowerCase().trim();

                const apiValues = {
                  login: login,
                  email: values.email.toLowerCase().trim(),
                  telegramId: values.telegramId.trim(),
                  password: values.password,
                };

                await AuthService.register(apiValues);

                setRegisteredLogin(login);
                setShowSuccessMessage(true);
                resetForm();
              } catch (error: any) {
                console.error("Error response:", error.response?.data);
                showToast(getErrorMessage(error), "error");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form className={styles.form}>
                <DynamicField
                  field={{
                    type: "string",
                    name: "nickname",
                    label: "nickname",
                    placeholder: "enterNickname",
                  }}
                />
                <DynamicField
                  field={{
                    type: "string",
                    name: "email",
                    label: "email",
                    placeholder: "enterEmail",
                  }}
                />
                <DynamicField
                  field={{
                    type: "string",
                    name: "telegramId",
                    label: "telegramId",
                    placeholder: "enterTelegramId",
                  }}
                />
                <p className={styles.helpText}>
                  <i className="fa fa-lightbulb-o"></i> {t("telegramIdHint")}{" "}
                  <a
                    href="https://t.me/Get_myidrobot"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.helpLink}
                  >
                    <i className="fa fa-telegram"></i> @Get_myidrobot
                  </a>
                </p>
                <p className={styles.helpText}>
                  <i className="fa fa-lightbulb-o"></i> {t("telegramTgHint")}{" "}
                  <a
                    href={`https://t.me/${tgBot?.substring(1)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.helpLink}
                  >
                    <i className="fa fa-telegram"></i> {tgBot}
                  </a>
                </p>
                <DynamicField
                  field={{
                    type: "password",
                    name: "password",
                    label: "password",
                    placeholder: "enterPassword",
                  }}
                />
                <DynamicField
                  field={{
                    type: "password",
                    name: "confirmPassword",
                    label: "confirmPassword",
                    placeholder: "confirmYourPassword",
                  }}
                />

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("loading") : t("registerTitle")}
                </button>

                <div className={styles.links}>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => onSwitchMode("login")}
                  >
                    {t("alreadyHaveAccount")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}